import { useRef, useState, useEffect, useCallback } from 'react';
import type { MouseEvent, KeyboardEvent as ReactKeyboardEvent, ChangeEvent } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';
import styled, { css } from 'styled-components';

import {
  Button,
  RadioButtonGroup,
  RadioButton,
  ColorPicker,
  defaultThemeProp,
  useI18n,
  useUID,
  Tooltip,
  TouchDialog,
  Popover,
  useOuterEvent,
  Text,
  Flex,
  calculateFontSize,
  isInstance,
  readableColor,
  isTouchDevice
} from '@pega/cosmos-react-core';

import useEscapeKey from '../hooks/useEscapeKey';
import useCloseOnEditorClick from '../hooks/useCloseOnEditorClick';

const StyledColorButton = styled(Button)(({ theme }) => {
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: calc(3.5 * ${theme.base.spacing});
    min-width: calc(3.5 * ${theme.base.spacing});
    padding: 0;
  `;
});

const StyledTextColorIcon = styled.span<{ $color?: string }>(({ theme, $color }) => {
  const fontSizes = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);
  return css`
    display: flex;
    flex-direction: column;
    align-items: center;
    font-weight: bold;
    font-size: ${fontSizes.s};
    line-height: 1;

    &::after {
      content: '';
      display: block;
      width: 100%;
      height: calc(0.375 * ${theme.base.spacing});
      background-color: ${$color || theme.base.palette['foreground-color']};
      border-radius: calc(0.125 * ${theme.base.spacing});
      margin-top: calc(0.125 * ${theme.base.spacing});
    }
  `;
});

StyledTextColorIcon.defaultProps = defaultThemeProp;

const StyledHighlightColorIcon = styled.span<{ $color?: string }>(({ theme, $color }) => {
  const fontSizes = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);
  const textColor = $color ? readableColor($color) : 'inherit';
  return css`
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: ${fontSizes.s};
    min-width: 1.25em;
    min-height: 1.25em;
    padding: 0.125em 0.25em;
    border: calc(0.125 * ${theme.base.spacing}) solid
      ${theme.components['form-control']['border-color']};
    border-radius: calc(0.25 * ${theme.base['border-radius']});
    background-color: ${$color || 'transparent'};
    color: ${textColor};
  `;
});

StyledHighlightColorIcon.defaultProps = defaultThemeProp;

StyledColorButton.defaultProps = defaultThemeProp;

const StyledPopoverContent = styled.div(({ theme }) => {
  return css`
    padding: ${theme.base.spacing};
    min-width: calc(14 * ${theme.base.spacing});
  `;
});

StyledPopoverContent.defaultProps = defaultThemeProp;

const StyledButtonGroup = styled.div<{ $touch?: boolean }>(({ theme, $touch }) => {
  return css`
    display: flex;
    gap: calc(0.5 * ${theme.base.spacing});
    justify-content: space-between;
    margin-top: auto;
    width: 100%;
    padding-block-end: ${$touch ? theme.base.spacing : 0};
  `;
});

StyledButtonGroup.defaultProps = defaultThemeProp;

const StyledColorPickerContent = styled(Flex)`
  flex: 1;
  min-height: 0;
`;

StyledColorPickerContent.defaultProps = defaultThemeProp;

interface ColorPickerButtonProps {
  editor: TiptapEditor;
  type: 'text' | 'highlight';
  'data-testid'?: string;
}

type ColorType = 'auto' | 'custom';

const ColorPickerButton = ({ editor, type, ...restProps }: ColorPickerButtonProps) => {
  const t = useI18n();
  const titleId = useUID();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [colorType, setColorType] = useState<ColorType>('custom');
  const [customColor, setCustomColor] = useState('#000000');

  // Focus first radio button when popover opens
  useEffect(() => {
    if (open) {
      // Use setTimeout to wait for popover to render
      const timer = setTimeout(() => {
        if (popoverRef.current) {
          const firstRadio =
            popoverRef.current.querySelector<HTMLInputElement>('input[type="radio"]');
          firstRadio?.focus();
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const getCurrentColor = (): string | undefined => {
    const attrs =
      type === 'text' ? editor.getAttributes('textStyle') : editor.getAttributes('highlight');
    const { color } = attrs;
    return typeof color === 'string' ? color : undefined;
  };

  const currentColor = getCurrentColor();

  const openMenu = () => {
    // Initialize state based on current color, default to custom
    if (currentColor) {
      setCustomColor(currentColor);
    } else {
      setCustomColor('#000000');
    }
    setColorType('custom');
    setOpen(true);
  };

  const resetMenu = () => {
    setOpen(false);
  };

  useOuterEvent('mousedown', [popoverRef, buttonRef, touchDialogRef], (event: Event) => {
    // Don't close if clicking inside a nested popover (like ColorPicker's palette)
    const { target } = event;
    if (isInstance(target, HTMLElement) && target.closest('[data-popper-placement]')) {
      return;
    }
    if (open) {
      resetMenu();
    }
  });

  const handleSubmit = () => {
    if (colorType === 'auto') {
      if (type === 'text') {
        editor.chain().focus().unsetColor().run();
      } else {
        editor.chain().focus().unsetHighlight().run();
      }
    } else if (type === 'text') {
      editor.chain().focus().setColor(customColor).run();
    } else {
      editor.chain().focus().setHighlight({ color: customColor }).run();
    }
    resetMenu();
  };

  const handleCancelKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' && open) {
      event.preventDefault();
      resetMenu();
      buttonRef.current?.focus();
    }
  };

  const handleCancelMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
    if (open) {
      event.preventDefault();
      resetMenu();
      buttonRef.current?.focus();
    }
  };

  const handleEscapeClose = useCallback(() => {
    resetMenu();
    buttonRef.current?.focus();
  }, []);

  useEscapeKey(open, handleEscapeClose, editor);
  useCloseOnEditorClick(open, handleEscapeClose);

  const label = type === 'text' ? t('rte_text_color') : t('rte_background_color');
  const modalTitle = type === 'text' ? t('rte_font_color') : t('rte_background_color');
  const isHighlight = type === 'highlight';
  const colorPickerContent = (
    <StyledColorPickerContent container={{ direction: 'column', gap: 1 }}>
      <RadioButtonGroup
        name='colorType'
        label={t('rte_color_type')}
        labelHidden
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          if (value === 'auto' || value === 'custom') {
            setColorType(value);
          }
        }}
        inline
      >
        <RadioButton label={t('rte_color_auto')} value='auto' checked={colorType === 'auto'} />
        <RadioButton
          label={t('rte_color_custom')}
          value='custom'
          checked={colorType === 'custom'}
        />
      </RadioButtonGroup>
      <ColorPicker
        label={t('rte_color')}
        labelHidden
        value={customColor}
        onChange={color => setCustomColor(color.hex)}
        disabled={colorType === 'auto'}
      />
      <StyledButtonGroup $touch={isTouchDevice}>
        <Button
          variant='secondary'
          onKeyDown={handleCancelKeyDown}
          onMouseDown={handleCancelMouseDown}
          type='button'
        >
          {t('cancel')}
        </Button>
        <Button variant='primary' onClick={handleSubmit}>
          {t('submit')}
        </Button>
      </StyledButtonGroup>
    </StyledColorPickerContent>
  );

  return (
    <>
      <StyledColorButton
        {...restProps}
        variant='simple'
        type='button'
        ref={buttonRef}
        data-toolbar-item='true'
        onMouseDown={(e: MouseEvent) => {
          e.preventDefault();
          openMenu();
        }}
        onKeyDown={(e: ReactKeyboardEvent) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            openMenu();
          }
        }}
        onBlur={(e: React.FocusEvent) => {
          const { relatedTarget } = e;
          if (
            isInstance(relatedTarget, Node) &&
            !buttonRef.current?.contains(relatedTarget) &&
            !popoverRef.current?.contains(relatedTarget) &&
            !touchDialogRef.current?.contains(relatedTarget)
          ) {
            resetMenu();
          }
        }}
        aria-label={label}
        aria-haspopup='dialog'
        aria-expanded={open}
      >
        {isHighlight ? (
          <StyledHighlightColorIcon $color={currentColor}>A</StyledHighlightColorIcon>
        ) : (
          <StyledTextColorIcon $color={currentColor}>A</StyledTextColorIcon>
        )}
      </StyledColorButton>
      {buttonRef.current && !open && (
        <Tooltip target={buttonRef.current} showDelay='none' hideDelay='none'>
          {label}
        </Tooltip>
      )}
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={open}
          heading={modalTitle}
          onDismiss={resetMenu}
          onAfterClose={() => buttonRef.current?.focus()}
        >
          {colorPickerContent}
        </TouchDialog>
      ) : (
        <Popover show={open} target={buttonRef.current} ref={popoverRef} placement='bottom'>
          <StyledPopoverContent role='dialog' aria-labelledby={titleId}>
            <Text variant='h4' id={titleId}>
              {modalTitle}
            </Text>
            {colorPickerContent}
          </StyledPopoverContent>
        </Popover>
      )}
    </>
  );
};

export default ColorPickerButton;
